import React, { useEffect, useRef, useCallback, useState } from "react";
import ButtonList from "../components/ButtonList";
import BackButton from "../components/BackButton";
import MicrophoneIcon from "../assets/microphone_icon.png";
import IsuzuTruckLogo from "../assets/isuzu_truck_logo.png";
import ListeningAnimation from "../assets/listening_animation.gif";
import { RealtimeClient } from "@openai/realtime-api-beta";
import { WavRecorder, WavStreamPlayer } from "../lib/wavtools/index.js";
import { instructions } from "../utils/conversation_config.js";
import { WavRenderer } from "../utils/wav_renderer.js";

import { useNavigate } from "react-router-dom";

const LOCAL_RELAY_SERVER_URL =
  process.env.REACT_APP_LOCAL_RELAY_SERVER_URL || "";

// Combined list of questions and audio files
const questionAudioPairs = [
  {
    question:
      "What kind of warranty and maintenance plans does Isuzu offer for its Ready-to-Work models?",
    audio: "warranty",
  },
  {
    question:
      "What are the main differences between the Isuzu N Series and F Series trucks?",
    audio: "comparison",
  },
  {
    question:
      "Can you explain the safety features available on the latest Isuzu F Series models?",
    audio: "safety",
  },
  {
    question:
      "What transmission options are offered across the Isuzu N Series trucks?",
    audio: "transmission",
  },
  {
    question:
      "How does the Isuzu N Series handle off-road conditions with its AWD and 4x4 options?",
    audio: "offroad",
  },
];

const Voice = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [responseData, setResponseData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetch("/assets/data.json")
      .then((response) => response.json())
      .then((jsonData) => {
        setResponseData(jsonData.responseData);
      })
      .catch((error) => console.error("Error fetching JSON:", error));
  }, []);

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef(new WavStreamPlayer({ sampleRate: 24000 }));
  const clientRef = useRef(
    new RealtimeClient(
      LOCAL_RELAY_SERVER_URL
        ? { url: LOCAL_RELAY_SERVER_URL }
        : {
            apiKey: apiKey,
            dangerouslyAllowAPIKeyInBrowser: true,
          }
    )
  );
  /**
   * References for
   * - Rendering audio visualization (canvas)
   */
  const serverCanvasRef = useRef(null);

  const apiKey = LOCAL_RELAY_SERVER_URL
    ? ""
    : localStorage.getItem("tmp::voice_api_key") ||
      prompt("OpenAI API Key") ||
      "";
  if (apiKey !== "") {
    localStorage.setItem("tmp::voice_api_key", apiKey);
  }

  /**
   * Connect to conversation:
   * WavRecorder takes speech input, WavStreamPlayer outputs, client is API client
   */
  const connectConversation = useCallback(async () => {
    const client = clientRef.current;
    const wavRecorder = wavRecorderRef.current;
    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Stop any audio that's currently playing (only the canned audios at this stage)
    wavStreamPlayerRef.current.stopAll();

    // Set state variables
    setIsConnected(true);

    // Connect to microphone
    await wavRecorder.begin();

    // Connect to audio output
    await wavStreamPlayer.connect();
    // Connect to realtime API
    await client.connect();
    client.sendUserMessageContent([
      {
        type: `input_text`,
        text: `Hello!`,
      },
    ]);

    await wavRecorder.record((data) => client.appendInputAudio(data.mono));

    console.log("Connected to conversation");
  }, []);

  /**
   * Disconnect and reset conversation state
   */
  const disconnectConversation = useCallback(async () => {
    setIsConnected(false);

    const client = clientRef.current;
    client.disconnect();

    const wavRecorder = wavRecorderRef.current;
    await wavRecorder.end();

    const wavStreamPlayer = wavStreamPlayerRef.current;
    await wavStreamPlayer.interrupt();

    console.log("Disconnected from conversation");
  }, []);

  // DrawRings
  useEffect(() => {
    let isLoaded = true;

    const wavRecorder = wavRecorderRef.current;

    const wavStreamPlayer = wavStreamPlayerRef.current;
    const serverCanvas = serverCanvasRef.current;
    let serverCtx = null; // Initialize the canvas rendering context

    const render = () => {
      if (isLoaded) {
        if (serverCanvas) {
          // Dynamically update canvas dimensions
          if (!serverCanvas.width || !serverCanvas.height) {
            serverCanvas.width = serverCanvas.offsetWidth;
            serverCanvas.height = serverCanvas.offsetHeight;
          }
          serverCtx = serverCtx || serverCanvas.getContext("2d");
          if (serverCtx) {
            // Clear the canvas
            serverCtx.clearRect(0, 0, serverCanvas.width, serverCanvas.height);

            // Get audio frequency data
            const result = wavStreamPlayer.analyser
              ? wavStreamPlayer.getFrequencies("voice")
              : { values: new Float32Array([0]) };

            // Draw the rings on the canvas
            WavRenderer.drawRings(
              serverCanvas,
              serverCtx,
              result.values, // Frequency data
              "#FFFFFF", // Ring color
              10, // Number of rings
              90 // Base radius
            );
          }
        }

        // FOR LISTENING ANIMATION ICON
        if (wavRecorder.recording) {
          // Periodically analyze frequencies
          try {
            const frequencies = wavRecorder.getFrequencies();
            // Compute the average from values
            const data = frequencies?.values || [];
            if (data.length > 0) {
              const averageVolume =
                Array.from(data).reduce((acc, val) => acc + val, 0) /
                data.length;
              setIsListening(averageVolume > 0.01); // Adjust threshold as needed
            } else {
              console.warn("No frequency values available.");
            }
          } catch (err) {
            console.error("Error analyzing frequencies:", err);
          }
        }

        window.requestAnimationFrame(render);
      }
    };

    render();

    return () => {
      isLoaded = false;
    };
  }, []);

  /**
   * Core RealtimeClient and audio capture setup
   */
  useEffect(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    client.updateSession({ instructions: instructions });

    // Ensure client is on voice activity detection mode
    client.updateSession({
      turn_detection: { type: "server_vad" },
    });

    // Handle realtime events from client + server for event logging
    client.on("error", (event) => console.error(event));
    client.on("conversation.interrupted", async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });
    client.on("conversation.updated", async ({ item, delta }) => {
      if (delta?.audio) {
        wavStreamPlayer.add16BitPCM(delta.audio, item.id);
      }
    });

    return () => {
      // Cleanup; resets to defaults
      client.reset();
    };
  }, []);

  const goBack = async () => {
    if (isConnected) {
      setIsConnected(false);

      const client = clientRef.current;
      client.disconnect();
  
      const wavRecorder = wavRecorderRef.current;
      await wavRecorder.end();
  
      const wavStreamPlayer = wavStreamPlayerRef.current;
      await wavStreamPlayer.interrupt();
  
      console.log("Disconnected from conversation");
    }
    
    wavStreamPlayerRef.current.stopAll();
    navigate("/");
  }

  return (
    <div className="d-flex justify-content-center align-items-center h-100 w-100 position-relative">
      <BackButton onClickHandler={goBack}/>
      <div className="col-4 d-flex align-items-center h-100">
        {responseData && (
          <ButtonList
            items={responseData}
            screen={"voice"}
            onClickHandler={async (audioPath) => {
              try {
                await wavStreamPlayerRef.current.connect(); // Ensure the player is connected
                wavStreamPlayerRef.current.playFile(audioPath); // Play the audio file
                if (isConnected) {
                  disconnectConversation(); // Disconnect conversation
                }
              } catch (error) {
                console.error("Error playing audio:", error);
              }
            }}
          />
        )}
      </div>
      <div className="col-8 idle-inner-frame voice-inner-frame">
        <div className="idle-voice-visual">
          <div className="visualization">
            <div className="visualization-entry server">
              <canvas ref={serverCanvasRef} width={700} height={700} />
              <img src={IsuzuTruckLogo} className="isuzu-truck-logo" />
            </div>
          </div>
          <div className="isuzu-btn-container idle-voice-btn">
            <button
              className="text-decoration-none isuzu-btn voice-icon-container"
              onClick={
                isConnected ? disconnectConversation : connectConversation
              }
            >
              {isConnected ? (
                isListening ? (
                  <img
                    src={ListeningAnimation}
                    alt="Listening Animation"
                    className=""
                  />
                ) : (
                  <div className="stop-icon"></div>
                )
              ) : (
                <img
                  src={MicrophoneIcon}
                  alt="Microphone Icon"
                  className="voice-icon"
                />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Voice;
